<template src="./TicketStatusHistory.html"></template>

<script>
import { getTicketStatus, formatDate } from '../../utils/computeds'

// XAN-24 — historial del ticket. XAN-193 (adjustments) — it used to render status changes only;
// it now renders the full audit trail. The component keeps its filename to match the backend
// entity, which kept `TicketStatusHistory` so the table would not have to be renamed.
//
// Rows written before the change carry no `eventType`: they are always status rows, so they fall
// back to CREATED/STATUS_CHANGED depending on whether `fromStatus` is set. Without that fallback
// the history already recorded in QA and production would render blank.
const EVENT = {
  CREATED: { icon: 'mdi-plus-circle-outline', color: 'secondary' },
  STATUS_CHANGED: { icon: 'mdi-swap-horizontal', color: 'secondary' },
  ASSIGNEE_CHANGED: { icon: 'mdi-account-arrow-right-outline', color: 'indigo' },
  TITLE_CHANGED: { icon: 'mdi-format-title', color: 'blue-grey' },
  DESCRIPTION_CHANGED: { icon: 'mdi-text', color: 'blue-grey' },
  PRIORITY_CHANGED: { icon: 'mdi-flag-outline', color: 'orange darken-2' },
  DUE_DATE_CHANGED: { icon: 'mdi-calendar-edit', color: 'orange darken-2' },
  PROJECT_CHANGED: { icon: 'mdi-folder-move-outline', color: 'teal' },
  COMMENT_ADDED: { icon: 'mdi-comment-outline', color: 'blue-grey' },
  ATTACHMENT_ADDED: { icon: 'mdi-paperclip', color: 'teal' },
  ATTACHMENT_DELETED: { icon: 'mdi-paperclip-remove', color: 'error' },
  DELETED: { icon: 'mdi-delete-outline', color: 'error' }
}

const FALLBACK = { icon: 'mdi-history', color: 'grey' }

// Una descripción larga vuelve la línea de tiempo ilegible; se recorta para que el evento se lea
// de un vistazo y el texto completo se consulte en el ticket.
const truncate = (value, max = 60) => {
  if (!value) return ''
  return value.length > max ? `${value.slice(0, max)}…` : value
}

export default {
  name: 'TicketStatusHistory',
  props: {
    history: {
      type: Array,
      default: () => []
    }
  },
  computed: {
    getTicketStatus,
    formatDate,
    sortedHistory () {
      return [...this.history]
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
        .map((item) => ({ ...item, ...this.describe(item) }))
    }
  },
  methods: {
    getFullName (person) {
      return person ? `${person.name} ${person.lastName || ''}`.trim() : 'Alguien'
    },
    eventTypeOf (item) {
      if (item.eventType) return item.eventType
      return item.fromStatus ? 'STATUS_CHANGED' : 'CREATED'
    },
    // Devuelve el ícono, el color y la frase ya armada para cada evento. Se resuelve acá y no en la
    // plantilla para no llenarla de v-if anidados por cada tipo.
    describe (item) {
      const type = this.eventTypeOf(item)
      const style = EVENT[type] || FALLBACK
      return { ...style, text: this.textFor(type, item) }
    },
    textFor (type, item) {
      switch (type) {
        case 'CREATED':
          return `Creó el ticket en ${this.getTicketStatus(item.toStatus)}`
        case 'STATUS_CHANGED':
          return `Cambió el estado de ${this.getTicketStatus(item.fromStatus)} a ${this.getTicketStatus(item.toStatus)}`
        case 'ASSIGNEE_CHANGED':
          if (item.oldValue === 'Sin asignar') return `Asignó el ticket a ${item.newValue}`
          if (item.newValue === 'Sin asignar') return `Quitó la asignación a ${item.oldValue}`
          return `Reasignó el ticket de ${item.oldValue} a ${item.newValue}`
        case 'TITLE_CHANGED':
          return `Cambió el título de «${truncate(item.oldValue)}» a «${truncate(item.newValue)}»`
        case 'DESCRIPTION_CHANGED':
          return `Actualizó la descripción: «${truncate(item.newValue)}»`
        case 'PRIORITY_CHANGED':
          return `Cambió la prioridad de ${item.oldValue} a ${item.newValue}`
        case 'DUE_DATE_CHANGED':
          return `Cambió la fecha límite de ${item.oldValue} a ${item.newValue}`
        case 'PROJECT_CHANGED':
          return `Movió el ticket de ${item.oldValue} a ${item.newValue}`
        case 'COMMENT_ADDED':
          return 'Agregó un comentario'
        case 'ATTACHMENT_ADDED':
          return `Adjuntó el archivo «${truncate(item.newValue)}»`
        case 'ATTACHMENT_DELETED':
          return `Eliminó el archivo adjunto «${truncate(item.oldValue)}»`
        case 'DELETED':
          return 'Eliminó el ticket'
        default:
          return 'Registró un cambio'
      }
    }
  }
}
</script>

<style lang="scss" src="./TicketStatusHistory.scss" scoped></style>
